<template>
    <div class="ew-data-table tw-bg-white">
        <div
            v-if="(filters || $slots['filters'] || searchable || slots['actions']) && sidebar"
            class="table-filters-container"
            :class="{
                'is-expanded': filtersExpanded,
            }"
        >
            <div class="table-actions tw-flex tw-flex-col tw-gap-1">
                <div class="tw-relative tw-z-10">
                    <Tooltip position="right" label="Search">
                        <Button
                            v-if="searchable"
                            :active="searchExpanded"
                            @click="
                                async () => {
                                    searchExpanded = !searchExpanded;
                                    await $nextTick();
                                    tableSearch?.focus();
                                }
                            "
                        >
                            <i v-if="searchExpanded" class="mdi mdi-close"></i>
                            <i v-else class="mdi mdi-magnify"></i>
                        </Button>
                    </Tooltip>
                </div>
                <Tooltip position="right" label="Filters">
                    <Button
                        v-if="filters || $slots['filters']"
                        :active="filtersExpanded"
                        @click="filtersExpanded = !filtersExpanded"
                    >
                        <i v-if="filtersExpanded" class="mdi mdi-chevron-left"></i>
                        <i v-else class="mdi mdi-filter-multiple-outline"></i>
                    </Button>
                </Tooltip>
                <Tooltip v-if="endpoint" position="right" label="Refresh">
                    <Button :active="isLoading" @click="refreshData()">
                        <div
                            :class="{
                                'tw-animate-spin': isLoading,
                            }"
                        >
                            <i class="mdi mdi-reload"></i>
                        </div>
                    </Button>
                </Tooltip>
                <slot name="actions" />
            </div>
            <div class="flex flex-col">
                <Textbox
                    v-if="searchExpanded"
                    ref="tableSearch"
                    v-model="searchQuery"
                    class="tw-absolute tw-w-[196px] tw-z-10 tw-h-[30px] tw-mt-2 tw-mr-1"
                    placeholder="Search..."
                />
                <div v-if="filtersExpanded" class="tw-float-left">
                    <h5 class="mt-3">Filter Results</h5>
                    <slot name="filters" />
                </div>
            </div>
        </div>

        <div
            class="table !tw-mb-0"
            :class="{
                'tw-overflow-x-auto': horizontalScroll,
            }"
        >
            <slot name="before" />
            <table
                class="table !tw-mb-0"
                :class="{
                    'is-centered': centered,
                }"
            >
                <colgroup>
                    <col v-if="checkable" :style="checkableColumnStyle" />
                    <col
                        v-for="column in visibleColumns"
                        :key="`col-${column.field}`"
                        :style="getColumnStyle(column)"
                    />
                </colgroup>
                <thead v-if="header">
                    <slot name="header-prepend" />
                    <tr>
                        <th v-if="checkable && !canCheckAll"></th>
                        <th v-else-if="checkable && tableData?.length && canCheckAll" width="42">
                            <Checkbox
                                v-if="getAllCheckableRows().length"
                                :key="modelValue.length + '-' + currentPage"
                                :model-value="
                                    getAllCheckableRows().find((r) => !modelValue.some((mr) => mr.id === r.id))
                                        ? false
                                        : true
                                "
                                @update:model-value="
                                    (v) => {
                                        if (v) {
                                            modelValue = modelValue.concat(
                                                getAllCheckableRows().filter(
                                                    (r) => !modelValue.some((mr) => mr.id === r.id)
                                                )
                                            );
                                        } else {
                                            modelValue = modelValue.filter(
                                                (r) => !getAllCheckableRows().some((mr) => mr.id === r.id)
                                            );
                                        }
                                    }
                                "
                            />
                        </th>
                        <th
                            v-for="column in visibleColumns"
                            :key="column.field"
                            :class="[
                                {
                                    'is-sortable': column.sortable,
                                },
                                column.headerClass,
                            ]"
                            class="tw-content-center"
                            :width="column.width ? column.width : null"
                            @click="
                                column.sortable
                                    ? toggleSort(
                                          column.field,
                                          sorts[column.field] === 'asc'
                                              ? 'desc'
                                              : sorts[column.field] === 'desc'
                                                ? null
                                                : 'asc'
                                      )
                                    : null
                            "
                        >
                            <slot
                                v-if="slots[`heading(${column.field})`]"
                                :column="column"
                                :name="`heading(${column.field})`"
                            />
                            <span v-else>{{ column.title }}</span>
                            <i v-if="sorts[column.field] === 'asc'" class="mdi mdi-arrow-up"></i>
                            <i v-else-if="sorts[column.field] === 'desc'" class="mdi mdi-arrow-down"></i>
                        </th>
                    </tr>
                </thead>
                <Draggable
                    v-if="props.draggable"
                    v-model="tableData"
                    tag="tbody"
                    class="tw-text-black"
                    item-key="id"
                    handle=".drag-handle"
                    @end="emit('reorder', tableData)"
                >
                    <template #item="{ element: row, index: rowIndex }">
                        <tr :class="[typeof rowClass === 'function' ? rowClass(row, rowIndex, false) : rowClass]">
                            <td
                                v-for="column of visibleColumns"
                                :key="column.field"
                                class="tw-content-center"
                                :class="column.class"
                            >
                                <slot
                                    :name="`column(${column.field})`"
                                    :row="row"
                                    :index="rowIndex"
                                    :column="column"
                                    :expanded="false"
                                >
                                    {{ row[column.field] }}
                                </slot>
                            </td>
                        </tr>
                    </template>
                </Draggable>
                <tbody v-else class="tw-text-black">
                    <slot name="prepend" />
                    <template v-for="(row, rowIndex) in tableData" :key="rowIndex">
                        <tr
                            v-if="!isLoading"
                            :class="[
                                typeof rowClass === 'function'
                                    ? rowClass(row, rowIndex, expandedRows.includes(rowIndex))
                                    : rowClass,
                                slots['expanded-row'] &&
                                (typeof rowExpandable === 'function' ? rowExpandable(row) : rowExpandable)
                                    ? 'tw-cursor-pointer hover:tw-bg-gray-50'
                                    : '',
                                expandedRows.includes(rowIndex) ? expandedClass : '',
                            ]"
                            @click="onRowClick(row, rowIndex)"
                        >
                            <td v-if="checkable" width="38" @click.stop>
                                <Checkbox
                                    :id="`check-row-${rowIndex}`"
                                    :disabled="rowCheckable && !rowCheckable(row)"
                                    :model-value="modelValue.some((r) => r.id === row.id)"
                                    @update:model-value="
                                        (v) => {
                                            const nextModelValue = v
                                                ? modelValue.some((r) => r.id === row.id)
                                                    ? modelValue
                                                    : [...modelValue, row]
                                                : modelValue.filter((r) => r.id !== row.id);

                                            modelValue = nextModelValue;
                                        }
                                    "
                                    @click.stop
                                />
                            </td>
                            <td
                                v-for="column of visibleColumns"
                                :key="column.field"
                                class="tw-content-center"
                                :class="column.class"
                            >
                                <slot
                                    :name="`column(${column.field})`"
                                    :row="row"
                                    :index="rowIndex"
                                    :column="column"
                                    :expanded="expandedRows.includes(rowIndex)"
                                >
                                    {{ row[column.field] }}
                                </slot>
                            </td>
                        </tr>
                        <tr v-if="expandedRows.includes(rowIndex)" class="expanded-row">
                            <td class="!tw-p-0" :colspan="visibleColumns.length + (checkable ? 1 : 0)">
                                <slot :name="`expanded-row`" :row="row" :index="rowIndex" />
                            </td>
                        </tr>
                        <slot name="row-append" :row="row" />
                    </template>
                    <tr v-if="isLoading">
                        <td :colspan="visibleColumns.length"><slot name="loading">Loading...</slot></td>
                    </tr>
                    <tr v-else-if="!isLoading && tableData?.length === 0 && !slots['append']">
                        <td :colspan="visibleColumns.length" class="tw-text-center">
                            <slot name="empty">No data available</slot>
                        </td>
                    </tr>
                    <slot name="append" />
                </tbody>
                <slot name="footer" />
            </table>

            <div v-if="endpoint" class="tw-grid tw-grid-cols-2 tw-gap-2 tw-border-t-2 tw-border-gray-300">
                <div v-if="totalItems > 0" class="tw-p-3 tw-flex tw-items-center tw-gap-1.5">
                    <Select v-model="perPage">
                        <option v-for="option in perPageOptions" :key="option" :value="option">
                            {{ option }}
                        </option>
                    </Select>
                    items per page
                    <span class="tw-text-xs tw-text-gray-400">
                        [{{ totalItems }} {{ totalItems == 1 ? 'item' : 'items' }} in total]
                    </span>
                </div>
                <div v-if="totalItems > perPage" class="tw-p-3 tw-col-start-2 tw-flex tw-justify-end">
                    <Paginator
                        :data="{
                            data: tableData,
                            per_page: perPage,
                            current_page: currentPage,
                            total: totalItems,
                        }"
                        @change-page="refreshData"
                    />
                </div>
            </div>
            <slot name="after" />
        </div>
    </div>
</template>

<script setup>
import Textbox from '@/js/components/controls/Textbox.vue';
import Button from '@/js/components/controls/Button.vue';
import Checkbox from '@/js/components/controls/Checkbox.vue';
import Select from '@/js/components/controls/Select.vue';
import Paginator from '@/js/components/controls/Paginator.vue';
import debounce from 'lodash/debounce';
import Tooltip from '@/js/components/Tooltip.vue';
import Draggable from 'vuedraggable';
import { onMounted, useSlots, ref, watch, computed, useTemplateRef } from 'vue';
import { capturePosthogEvent } from '@/js/composables/usePosthog.js';

const props = defineProps({
    endpoint: {
        type: String,
        required: false,
    },
    data: {
        type: [Array, Object],
        required: false,
    },
    rowClass: {
        type: [Function, String],
        required: false,
    },
    expandedClass: {
        type: String,
        required: false,
    },
    rowCheckable: {
        type: Function,
        required: false,
    },
    rowExpandable: {
        type: [Boolean, Function],
        default: true,
    },
    centered: {
        type: Boolean,
        default: false,
    },
    canCheckAll: {
        type: Boolean,
        default: true,
    },
    perPageOptions: {
        type: Array,
        default: () => [10, 25, 40, 50],
    },
    filters: {
        type: Object,
        required: false,
    },
    searchable: {
        type: Boolean,
        default: false,
    },
    searchableString: {
        type: String,
        default: null,
    },
    checkable: {
        type: Boolean,
        default: false,
    },
    header: {
        type: Boolean,
        default: true,
    },
    sidebar: {
        type: Boolean,
        default: true,
    },
    columns: {
        type: Array,
        required: true,
    },
    horizontalScroll: {
        type: Boolean,
        default: false,
    },
    draggable: {
        type: Boolean,
        default: false,
    },
});
const tableSearch = useTemplateRef('tableSearch');
const slots = useSlots();
const modelValue = defineModel({
    type: Array,
    default: () => [],
});
const emit = defineEmits(['select', 'checked', 'update:modelValue', 'reorder']);
const isLoading = ref(false);
const tableData = ref(props.data ? (Array.isArray(props.data) ? props.data : Object.values(props.data)) : []);
const hasLoadedData = ref(!props.endpoint);
const currentPage = ref(1);
const perPage = ref(props.perPageOptions[0] ?? 10);
const totalItems = ref(0);
const sorts = ref({});
const filtersExpanded = ref(false);
const searchQuery = ref(null);
const searchExpanded = ref(false);
const expandedRows = ref([]);
const visibleColumns = computed(() => {
    return props.columns.filter((column) => !column.hidden);
});
const showingEmptyState = computed(
    () => hasLoadedData.value && !isLoading.value && tableData.value?.length === 0 && !slots['append']
);
watch(
    showingEmptyState,
    (showing) => {
        if (showing) {
            capturePosthogEvent('empty_state_viewed', { source: 'data_table' });
        }
    },
    { immediate: true, flush: 'post' }
);

const normaliseColumnWidth = (width) => {
    if (width === undefined || width === null || width === '') {
        return null;
    }

    return typeof width === 'number' ? `${width}px` : String(width);
};

const getColumnStyle = (column) => {
    const width = normaliseColumnWidth(column.width);

    if (!width) {
        return null;
    }

    return {
        width,
        minWidth: width,
        maxWidth: width,
    };
};

const checkableColumnStyle = computed(() => {
    if (!props.checkable) {
        return null;
    }

    return {
        width: '42px',
        minWidth: '42px',
        maxWidth: '42px',
    };
});
watch(
    () => props.data,
    (newVal) => {
        tableData.value = Array.isArray(newVal) ? newVal : Object.values(newVal);
        hasLoadedData.value = true;
    }
);
watch(perPage, () => {
    refreshData(1);
});
watch(
    () => props.filters,
    () => {
        refreshData();
    },
    { deep: true }
);
watch(
    () => props.searchableString,
    (newVal) => {
        searchQuery.value = newVal;
    }
);
watch(searchQuery, () => {
    debouncedRefreshData();
});
watch(searchExpanded, (newVal) => {
    if (!newVal) {
        searchQuery.value = null;
    }
});
watch(
    () => props.endpoint,
    () => {
        refreshData();
    }
);
watch(
    sorts,
    () => {
        sortData();
    },
    { deep: true }
);
onMounted(() => {
    if (!tableData.value && !props.endpoint) {
        return;
    }
    if (props.endpoint) {
        refreshData();
        return;
    }
});
const onRowClick = (row, index) => {
    if (slots['expanded-row']) {
        expandedRows.value.includes(index)
            ? expandedRows.value.splice(expandedRows.value.indexOf(index), 1)
            : expandedRows.value.push(index);
    }
    emit('select', row);
};
const refreshData = (newPage = null) => {
    if (!props.endpoint) {
        return;
    }
    if (!newPage) {
        newPage = currentPage.value;
    }
    isLoading.value = true;
    const url = new URL(props.endpoint);
    url.searchParams.append('paginated', 1);
    url.searchParams.append('perPage', perPage.value);
    url.searchParams.append('page', newPage ?? currentPage.value);
    if ((props.searchable || props.searchableString) && searchQuery.value) {
        url.searchParams.append('query', searchQuery.value);
    }
    if (Object.keys(sorts.value).length) {
        url.searchParams.append(
            'sort',
            Object.keys(sorts.value)
                .map((key) => `${key}=${sorts.value[key]}`)
                .join(',')
        );
    }
    if (props.filters && Object.keys(props.filters).length) {
        const filterString = Object.keys(props.filters)
            .map((key) => {
                if (Array.isArray(props.filters[key])) {
                    return `${key}=${props.filters[key].join(',')}`;
                }
                if (!props.filters[key]) {
                    return null;
                }
                return `${key}=${props.filters[key]}`;
            })
            .filter((f) => f)
            .join(';');
        if (filterString) {
            url.searchParams.append('filter', filterString);
        }
    }
    axios
        .get(url.toString())
        .then(({ data }) => {
            tableData.value = data.data;
            hasLoadedData.value = true;
            currentPage.value = data.current_page;
            totalItems.value = data.total;
        })
        .finally(() => (isLoading.value = false));
};
const debouncedRefreshData = debounce(function () {
    currentPage.value = 1;
    refreshData();
}, 300);
const toggleSort = (field, order = null) => {
    sorts.value = {};
    if (order) {
        sorts.value[field] = order;
    } else {
        delete sorts.value[field];
    }
    refreshData();
};
const sortData = () => {
    // We don't want to do manual sorting if data is server side paginated
    if (props.endpoint) return;
    // sort the tabledata asc/desc by column/field

    // Pick out the column name and index to sort on
    const sortingColumn = Object.keys(sorts.value)[0];
    const sortDirection = Object.values(sorts.value)[0];
    if (sortDirection === 'desc') {
        tableData.value.sort((item1, item2) =>
            item1[sortingColumn] < item2[sortingColumn] ? 1 : item1[sortingColumn] > item2[sortingColumn] ? -1 : 0
        );
    } else if (sortDirection === 'asc') {
        tableData.value.sort((item1, item2) =>
            item1[sortingColumn] > item2[sortingColumn] ? 1 : item1[sortingColumn] < item2[sortingColumn] ? -1 : 0
        );
    }
};
function getAllCheckableRows() {
    if (!props.checkable) {
        return [];
    }
    if (!props.rowCheckable) {
        return tableData.value;
    }
    return tableData.value.filter((row) => props.rowCheckable(row));
}

defineExpose({
    tableData,
    totalItems,
    expandedRows,
    refreshData,
    debouncedRefreshData,
    isLoading,
});
</script>

<style lang="scss" scoped>
@use '@/assets/sass/eventbooks/variables' as variables;

.ew-data-table {
    display: flex;

    .table-filters-container {
        display: flex;
        width: 52px;
        border-right: 1px solid rgba(black, 0.1);

        .table-filters {
            flex: 1;
            padding-left: 5px;
        }
        .table-actions {
            padding: 6px;
        }

        &.is-expanded {
            width: 300px;
        }
    }

    .table {
        th,
        td {
            padding: 0.5rem 0.75rem;
        }

        th {
            user-select: none;
            color: black;
            font-weight: 500;

            &.is-sortable {
                @apply tw-text-eventwise-link;
                opacity: 0.9;
                cursor: pointer;

                &:hover {
                    opacity: 1;
                    background: rgba(black, 0.025);
                }
            }
        }
        &.is-centered {
            td {
                vertical-align: middle;
            }
        }
    }

    .expanded-row > td:empty {
        border: none;
    }
}
</style>
